import { type ReactNode, useCallback, useEffect, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";
import { colocaToast } from "@/shared/utils/toast";

import { AlreadyVerifiedScreen } from "../components/AlreadyVerifiedScreen";
import { CaptureStep } from "../components/CaptureStep";
import { ConfirmSummary } from "../components/ConfirmSummary";
import { ConsentGate } from "../components/ConsentGate";
import { InvalidLinkScreen } from "../components/InvalidLinkScreen";
import { ProcessingScreen } from "../components/ProcessingScreen";
import { PublicVerificationHeader } from "../components/PublicVerificationHeader";
import { ResultScreen } from "../components/ResultScreen";
import { StepIntroCard } from "../components/StepIntroCard";
import { TrustFooter } from "../components/TrustFooter";
import { VerificationLayout } from "../components/VerificationLayout";
import { VerificationStepper } from "../components/VerificationStepper";
import { useVerificationToken } from "../context/VerificationTokenContext";
import { usePublicVerificationExit } from "../hooks/usePublicVerificationExit";
import { useVerificationEngine } from "../hooks/useVerificationEngine";
import { useVerificationSession } from "../hooks/useVerificationSession";
import { useVerificationStatusPolling } from "../hooks/useVerificationStatusPolling";
import { VerificationService } from "../services/verificationService";
import type {
  VerificationConsentRequest,
  VerificationSessionResponse,
  VerificationStatusResponse,
} from "../types/verification-api.types";

/** Full-screen frame for the public link (marco B): minimal header + centered card + trust footer. */
function PublicFrame({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-muted">
      <PublicVerificationHeader />
      <main className="flex flex-1 items-start justify-center px-4 py-6 sm:items-center sm:py-10">
        {children}
      </main>
      <TrustFooter />
    </div>
  );
}

/** The engine for a resolved session. Split out so its hooks run only once the session is available. */
function PublicVerificationFlow({ token, session }: { token: string; session: VerificationSessionResponse }) {
  const { t } = useTranslations("verification");
  const purpose = session.purpose;
  const engine = useVerificationEngine({ purpose, frame: "public" });
  const { goTo, applyStatus } = engine;

  const [lastStatus, setLastStatus] = useState<VerificationStatusResponse | null>(null);
  const [consentSubmitting, setConsentSubmitting] = useState(false);

  // Pull the rich status once; if already terminal / in review, jump straight there.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await VerificationService.getStatusPublic(token);
        if (cancelled) return;
        setLastStatus(data);
        if (data.status !== "PENDING") applyStatus(data.status);
      } catch {
        // No status yet — stay on the intro screen.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, applyStatus]);

  const pollingEnabled = engine.screen === "processing" || engine.result === "review";
  useVerificationStatusPolling(
    async () => (await VerificationService.getStatusPublic(token)).data,
    pollingEnabled,
    (data) => {
      setLastStatus(data);
      applyStatus(data.status);
    },
  );

  const handleConsent = useCallback(
    async (values: VerificationConsentRequest) => {
      setConsentSubmitting(true);
      try {
        await VerificationService.submitConsent(token, values);
        goTo(purpose === "KYC" ? "data" : "company");
      } catch {
        colocaToast.error(t("error_generic_description"));
      } finally {
        setConsentSubmitting(false);
      }
    },
    [token, purpose, goTo, t],
  );

  const exit = usePublicVerificationExit();

  // Two separate questions, deliberately not collapsed into one flag.
  //
  // WORDING is about who is reading: with no session this is a client or a cardholder with no account here,
  // so "ya puedes continuar" would be a promise to nowhere. ACTION is about where they can go: a signed-in
  // member whose scopes reach no visible page has nowhere either, but calling them a stranger would be wrong
  // — they get the normal sentence and, like everyone with no destination, the closing line instead of a
  // button. Reading both off `landingPath` conflated them.
  const anonymous = !exit.hasSession;
  const canGoHome = exit.landingPath !== null;

  const resultPrimary =
    engine.result === "rejected"
      ? () => goTo(purpose === "KYC" ? "capture-doc" : "capture")
      : engine.result === "verified" && canGoHome
        ? exit.goHome
        : undefined;

  const renderScreen = () => {
    switch (engine.screen) {
      case "intro":
        return <StepIntroCard purpose={purpose} onStart={() => goTo("consent")} />;
      case "consent":
        return <ConsentGate purpose={purpose} submitting={consentSubmitting} onAccept={handleConsent} />;
      case "data":
      case "company":
        return (
          <ConfirmSummary
            subject={session.subject}
            onConfirm={() => goTo(purpose === "KYC" ? "capture-doc" : "capture")}
          />
        );
      case "capture-doc":
        // Single KYC capture: the embedded SDK runs document + selfie/liveness in one session, so we go
        // straight to processing on submit (no separate selfie screen).
        return (
          <CaptureStep
            start={() => VerificationService.startCapturePublic(token).then((r) => r.data)}
            mode="document"
            onComplete={() => goTo("processing")}
          />
        );
      case "capture":
        return (
          <CaptureStep
            start={() => VerificationService.startCapturePublic(token).then((r) => r.data)}
            mode="company"
            onComplete={() => goTo("processing")}
          />
        );
      case "processing":
        return <ProcessingScreen purpose={purpose} />;
      case "result":
        return (
          <ResultScreen
            result={engine.result ?? "review"}
            rejectionReason={lastStatus?.rejection_reason}
            onPrimary={resultPrimary}
            descriptionKey={
              anonymous && engine.result === "verified" ? "result_verified_description_external" : undefined
            }
            noteKey={
              // `review` is the same dead end: "we'll email you" and nowhere to go. Both get the close line,
              // and so does anyone else this screen cannot hand an action to.
              !canGoHome && (engine.result === "verified" || engine.result === "review")
                ? "verified_close_window_note"
                : undefined
            }
          />
        );
      default:
        return <ProcessingScreen purpose={purpose} />;
    }
  };

  return (
    <VerificationLayout
      size={purpose === "KYB" ? "business" : "person"}
      stepper={
        engine.screen === "intro" ? undefined : (
          <VerificationStepper purpose={purpose} currentStep={engine.step} totalSteps={engine.totalSteps} />
        )
      }
    >
      {renderScreen()}
    </VerificationLayout>
  );
}

/** Marco B page — resolves the link token into a session, then mounts the engine. Default export (routed). */
export default function PublicVerificationPage() {
  const token = useVerificationToken();
  const { session, isLoading, errorKind } = useVerificationSession(token);
  const { t } = useTranslations("verification");
  const exit = usePublicVerificationExit();

  if (isLoading) {
    return (
      <PublicFrame>
        <VerificationLayout>
          <div className="space-y-4">
            <ColocaSkeleton className="mx-auto size-12 rounded-full" />
            <ColocaSkeleton className="mx-auto h-7 w-2/3" />
            <ColocaSkeleton className="h-40 w-full" />
          </div>
        </VerificationLayout>
      </PublicFrame>
    );
  }

  if (errorKind || !session) {
    return (
      <PublicFrame>
        <VerificationLayout>
          <InvalidLinkScreen kind={errorKind === "expired" ? "expired" : "invalid"} />
        </VerificationLayout>
      </PublicFrame>
    );
  }

  if (session.status === "VERIFIED") {
    return (
      <PublicFrame>
        <VerificationLayout>
          <AlreadyVerifiedScreen
            note={exit.landingPath === null ? t("verified_close_window_note") : undefined}
            action={
              exit.landingPath ? (
                <ColocaButton variant="primary" onClick={exit.goHome}>
                  {t("result_verified_cta")}
                </ColocaButton>
              ) : undefined
            }
          />
        </VerificationLayout>
      </PublicFrame>
    );
  }

  return (
    <PublicFrame>
      <PublicVerificationFlow token={token} session={session} />
    </PublicFrame>
  );
}
